import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users, Fuel, Cog, Gauge, Calendar, Check } from "lucide-react";
import { getCar } from "../lib/api";
import { imgUrl } from "../lib/img";

export default function CarDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    getCar(slug).then(setCar).catch(() => navigate("/fleet"));
  }, [slug, navigate]);

  if (!car) return <div className="pt-40 text-center text-white/40">Loading…</div>;

  const images = [car.image, ...(car.gallery || [])];
  const specs = [
    { icon: Users, label: "Seats", value: car.seats },
    { icon: Gauge, label: "Fuel Economy", value: `${car.mpg} MPG` },
    { icon: Cog, label: "Transmission", value: car.transmission },
    { icon: Fuel, label: "Fuel Type", value: car.fuel },
    { icon: Calendar, label: "Year", value: car.year },
  ];

  return (
    <div data-testid="car-detail-page" className="pt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <button
          data-testid="back-to-fleet-btn"
          onClick={() => navigate("/fleet")}
          className="flex items-center gap-2 text-white/50 hover:text-white text-sm"
        >
          <ArrowLeft size={16} /> Back to fleet
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 pb-16">
        {/* Gallery */}
        <div>
          <motion.div
            key={active}
            initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
            className="aspect-[4/3] bg-[#0F0F0F] border border-white/10 overflow-hidden"
          >
            <img src={imgUrl(images[active])} alt={car.name} className="w-full h-full object-cover" />
          </motion.div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {images.map((img, i) => (
                <button
                  key={img}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setActive(i)}
                  className={`w-24 h-16 overflow-hidden border ${active === i ? "border-[#D4AF37]" : "border-white/10"}`}
                >
                  <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <span className="border border-white/20 bg-black text-[10px] uppercase tracking-wider px-3 py-1">
            {car.type}
          </span>
          <p className="text-white/40 text-sm uppercase tracking-wider mt-5">{car.brand}</p>
          <h1 className="font-serif text-5xl tracking-tight mt-1">{car.name}</h1>
          <p className="text-white/60 leading-relaxed mt-6">{car.description}</p>

          <div className="mt-6">
            <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Approved for</p>
            <div className="flex flex-wrap gap-2">
              {car.rideshare?.map((p) => (
                <span key={p} data-testid={`rideshare-badge-${p}`} className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-white/80 border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-3 py-1.5">
                  <Check size={12} className="text-[#D4AF37]" /> {p}
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-end gap-6 mt-8">
            <div>
              <span className="text-[#D4AF37] text-4xl font-light">${car.price_per_week}</span>
              <span className="text-white/40 ml-1">/ week</span>
            </div>
            <div className="text-white/50 text-sm mb-1">or ${car.price_per_day}/day · unlimited miles</div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 mt-8 border-t border-l border-white/10">
            {specs.map((s) => (
              <div key={s.label} className="border-b border-r border-white/10 p-5">
                <s.icon size={18} className="text-[#D4AF37]" strokeWidth={1.4} />
                <p className="text-white/40 text-xs uppercase tracking-wider mt-3">{s.label}</p>
                <p className="text-white text-base mt-1">{s.value}</p>
              </div>
            ))}
          </div>

          <button
            data-testid="book-now-btn"
            onClick={() => navigate(`/book/${car.slug}`)}
            className="w-full mt-10 bg-white text-black py-4 text-base hover:bg-[#D4AF37] transition-colors"
          >
            Rent This Car
          </button>
          <p className="text-center text-white/40 text-xs mt-3">Pay at pickup · No deposit · Cancel anytime</p>
        </div>
      </div>
    </div>
  );
}
